import { Box, Button, Modal, Text } from '@ksyos/design-system';
import { useState } from 'react';
import { SERIAL_NUMBER_LENGTH } from '../../../../common/constants';
import { TwoFactorAuthenticationType } from '../../../../common/enums/twoFactorAuthenticationType';
import { translateKey } from '../../../translate';
import { HardwareTokenInfo } from '../notLoggedIn/HardwareTokenInfo';
import { HardwareTokenSerialNumber } from '../notLoggedIn/HardwareTokenSerialNumber';
import { MfaAppsInfo } from '../notLoggedIn/MfaAppsInfo';
import { MfaCodeInput } from '../notLoggedIn/MfaCodeInput';
import { MfaMethodSelector } from '../notLoggedIn/MfaMethodSelector';
import { MfaQrCodeForm } from '../notLoggedIn/MfaQrCodeForm';
import { MfaSuccessScreen } from '../notLoggedIn/MfaSuccessScreen';

export function SetMfaModal(props: {
    handleChangeOtpMethod: (method: TwoFactorAuthenticationType) => void;
    handleClose: () => void;
    isOpen: boolean;
}) {
    const [mfaType, setMfaType] = useState<TwoFactorAuthenticationType>(TwoFactorAuthenticationType.verificationApp);
    const [mfaWizardStep, setMfaWizardStep] = useState<number>(0);
    const [showVerificationAppFlow, setShowVerificationAppFlow] = useState<boolean>(false);
    const [showHardwareTokenFlow, setShowHardwareTokenFlow] = useState<boolean>(false);
    const [otpSecret, setOtpSecret] = useState<string>();
    const [serialNumber, setSerialNumber] = useState<string>('');
    const [wrongCodeCounter, setWrongCodeCounter] = useState<number>(0);
    const [isFormDisabled, setIsFormDisabled] = useState<boolean>(false);

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={props.handleClose}
            size="medium"
        >
            <Modal.Header>Tweestapsverificatie instellen</Modal.Header>
            <Modal.CloseButton></Modal.CloseButton>
            <Modal.Body>
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={8}
                >
                    {!showVerificationAppFlow && !showHardwareTokenFlow && (
                        <>
                            <Text>
                                Tweestapsverificatie is verplicht
                                <br />
                                <br />
                                Het instellen is zo gedaan.
                                <br />
                                Kies de methode die je gaat gebruiken.
                            </Text>
                            <MfaMethodSelector
                                onChange={setMfaType}
                                value={mfaType}
                            />
                        </>
                    )}

                    {showVerificationAppFlow && (
                        <>
                            {mfaWizardStep === 0 && <MfaAppsInfo />}
                            {mfaWizardStep === 1 && (
                                <MfaQrCodeForm onOtpSecretChange={(newOtp) => setOtpSecret(newOtp)} />
                            )}
                            {mfaWizardStep === 2 && otpSecret && (
                                <MfaCodeInput
                                    onCodeVerified={onMfaVerified}
                                    otpSecret={otpSecret}
                                />
                            )}
                            {mfaWizardStep === 3 && (
                                <MfaSuccessScreen mfaType={TwoFactorAuthenticationType.verificationApp} />
                            )}
                        </>
                    )}

                    {showHardwareTokenFlow && (
                        <>
                            {mfaWizardStep === 0 && <HardwareTokenInfo />}

                            {mfaWizardStep === 1 && (
                                <HardwareTokenSerialNumber
                                    onValidSerialNumber={(newSerialNumber) => setSerialNumber(newSerialNumber)}
                                    placeHolder={serialNumber}
                                    setIsFormDisabled={(disabled: boolean) => setIsFormDisabled(disabled)}
                                />
                            )}
                            {mfaWizardStep === 2 && (
                                <MfaCodeInput
                                    mfaType={TwoFactorAuthenticationType.verificationDevice}
                                    onCodeFailed={() => setWrongCodeCounter(wrongCodeCounter + 1)}
                                    onCodeVerified={onMfaVerified}
                                    serial={serialNumber}
                                    wrongCodeCounter={wrongCodeCounter}
                                />
                            )}
                            {mfaWizardStep === 3 && (
                                <MfaSuccessScreen mfaType={TwoFactorAuthenticationType.verificationDevice} />
                            )}
                        </>
                    )}
                </Box>
            </Modal.Body>
            <Modal.Footer>{getModalButtons()}</Modal.Footer>
        </Modal>
    );

    function onMfaVerified() {
        setIsFormDisabled(true);
        setTimeout(() => {
            setMfaWizardStep(mfaWizardStep + 1);
        }, 2000);
        props.handleChangeOtpMethod(
            showVerificationAppFlow
                ? TwoFactorAuthenticationType.verificationApp
                : TwoFactorAuthenticationType.verificationDevice
        );
    }

    function getModalButtons() {
        const buttons: JSX.Element[] = [];
        if (showVerificationAppFlow) {
            if (mfaWizardStep === 0) {
                buttons.push(
                    <Button
                        onClick={() => setShowVerificationAppFlow(false)}
                        variant="outline"
                    >
                        {translateKey('common.previous')}
                    </Button>
                );
            }

            if (mfaWizardStep > 0 && mfaWizardStep < 3) {
                buttons.push(
                    <Button
                        isDisabled={isFormDisabled}
                        onClick={() => setMfaWizardStep(mfaWizardStep - 1)}
                        variant="outline"
                    >
                        {translateKey('common.previous')}
                    </Button>
                );
            }
            if (mfaWizardStep < 2) {
                buttons.push(
                    <Button onClick={() => setMfaWizardStep(mfaWizardStep + 1)}>{translateKey('common.next')}</Button>
                );
            }
        } else if (showHardwareTokenFlow) {
            if (mfaWizardStep < 3) {
                buttons.push(
                    <Button
                        key="prev"
                        onClick={() => {
                            if (mfaWizardStep === 0) {
                                setShowHardwareTokenFlow(false);
                            } else {
                                setMfaWizardStep(mfaWizardStep - 1);
                                setIsFormDisabled(false);
                            }
                        }}
                        variant="outline"
                    >
                        {translateKey('common.previous')}
                    </Button>
                );
            }
            if (mfaWizardStep < 2) {
                buttons.push(
                    <Button
                        isDisabled={
                            isFormDisabled || (mfaWizardStep === 1 && serialNumber.length !== SERIAL_NUMBER_LENGTH)
                        }
                        key="next"
                        onClick={() => setMfaWizardStep(mfaWizardStep + 1)}
                    >
                        {translateKey('common.next')}
                    </Button>
                );
            }
        } else {
            buttons.push(
                <Button
                    onClick={() =>
                        mfaType === TwoFactorAuthenticationType.verificationApp
                            ? setShowVerificationAppFlow(true)
                            : setShowHardwareTokenFlow(true)
                    }
                >
                    {translateKey('common.next')}
                </Button>
            );
        }

        return buttons;
    }
}
